import type { TaskRow } from '@/lib/schedule';

/**
 * A sub-task counts as "done" when it is DONE **or** CANCELLED.
 *
 * Lifted verbatim from History, which shipped this convention first, so the
 * Schedule and History cards can never disagree about the same event's
 * counter (YAR-297).
 *
 * Worth revisiting on its own: on an upcoming event, rendering `2/3` because
 * one sub-task was *cancelled* overstates progress — cancelled is not
 * completed. Changing it would move numbers QA has already signed off on in
 * History, so it is deliberately left alone here.
 */
export function isTaskDone(status: string): boolean {
  return status === 'DONE' || status === 'CANCELLED';
}

/**
 * Groups tasks by the event they hang off. Tasks with no `related_event_id`
 * are skipped.
 */
export function groupSubtasksByEvent(tasks: TaskRow[]): Map<string, TaskRow[]> {
  const map = new Map<string, TaskRow[]>();
  for (const t of tasks) {
    if (!t.related_event_id) continue;
    const bucket = map.get(t.related_event_id);
    if (bucket) bucket.push(t);
    else map.set(t.related_event_id, [t]);
  }
  return map;
}

/**
 * The `{ done, total }` an EventCard needs, or `undefined` when the event has
 * no sub-tasks — the card renders nothing in that case, so an event without
 * sub-tasks must not get a `0/0` band.
 */
export function buildSubtaskSummary(
  subs: TaskRow[] | undefined,
): { done: number; total: number } | undefined {
  if (!subs || subs.length === 0) return undefined;
  return {
    done: subs.filter((t) => isTaskDone(t.status)).length,
    total: subs.length,
  };
}
